import React, { useState, useEffect } from 'react';
import { ChevronLeft, Save, AlertCircle, Shield } from 'lucide-react';
import { usersApi, RegisterDto, UpdateUserDto, permissionsApi, RoleDto } from '../../../services/api';

interface UserFormProps {
  userId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const UserForm: React.FC<UserFormProps> = ({ userId, onClose, onSuccess }) => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('User');
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(userId ? true : false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRoles();
    if (userId) {
      loadUser();
    }
  }, [userId]);

  const loadRoles = async () => {
    try {
      const rolesData = await permissionsApi.getAllRoles();
      setRoles(rolesData);
    } catch (err) {
      console.error('Error loading roles:', err);
    }
  };

  const loadUser = async () => {
    try {
      setIsFetching(true);
      setError(null);
      const user = await usersApi.getById(userId!);
      setUsername(user.username || '');
      setEmail(user.email || '');
      setFullName(user.fullName || '');
      setRole(user.role || 'User');
    } catch (err: any) {
      console.error('Error loading user:', err);
      setError(err.message || 'Error al cargar el usuario');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!userId && !username.trim()) {
      alert('Por favor ingrese el nombre de usuario');
      return;
    }

    if (!email.trim()) {
      alert('Por favor ingrese el email');
      return;
    }

    if (!userId && !password.trim()) {
      alert('Por favor ingrese la contraseña');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (userId) {
        // Update existing user
        const data: UpdateUserDto = {
          fullName: fullName.trim() || null,
          email: email.trim(),
          role: role || null
        };
        await usersApi.update(userId, data);
        alert('Usuario actualizado correctamente');
      } else {
        // Create new user
        const data: RegisterDto = {
          username: username.trim(),
          email: email.trim(),
          password: password.trim(),
          fullName: fullName.trim() || null,
          role: role || null
        };
        await usersApi.create(data);
        alert('Usuario creado correctamente');
      }
      onSuccess();
    } catch (err: any) {
      console.error('Error saving user:', err);
      setError(err.message || 'Error al guardar el usuario');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mb-4"></div>
          <p className="text-slate-600">Cargando usuario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm border border-green-100 overflow-hidden">
        {/* Header */}
        <div className="bg-green-50/50 px-6 py-6 border-b border-green-100 flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 hover:bg-green-100 rounded-lg transition-colors text-green-600"
            title="Volver"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h2 className="text-xl font-bold text-green-900">
            {userId ? 'Editar Usuario' : 'Nuevo Usuario'}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="text-red-600 h-5 w-5 flex-shrink-0" />
              <div>
                <p className="text-red-800 font-medium">Error al guardar</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Username - only for new users */}
          {!userId && (
            <div>
              <label htmlFor="username" className="block text-sm font-semibold text-green-800 mb-1">
                Nombre de Usuario *
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ej. juanperez"
                className="w-full bg-white border border-green-200 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5 h-12"
              />
            </div>
          )}

          {/* Username display - for editing */}
          {userId && (
            <div>
              <label className="block text-sm font-semibold text-green-800 mb-1">
                Nombre de Usuario
              </label>
              <div className="w-full bg-slate-100 border border-slate-200 text-slate-600 text-sm rounded-lg p-2.5 h-12 flex items-center">
                {username}
              </div>
              <p className="text-xs text-slate-500 mt-1">El nombre de usuario no se puede modificar</p>
            </div>
          )}

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-green-800 mb-1">
              Email *
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Ej. juan@ejemplo.com"
              className="w-full bg-white border border-green-200 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5 h-12"
            />
          </div>

          {/* Password - only for new users */}
          {!userId && (
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-green-800 mb-1">
                Contraseña *
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingrese una contraseña segura"
                className="w-full bg-white border border-green-200 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5 h-12"
              />
            </div>
          )}

          {/* Full Name */}
          <div>
            <label htmlFor="fullName" className="block text-sm font-semibold text-green-800 mb-1">
              Nombre Completo
            </label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Ej. Juan Pérez García"
              className="w-full bg-white border border-green-200 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5 h-12"
            />
          </div>

          {/* Role */}
          <div>
            <label htmlFor="role" className="block text-sm font-semibold text-green-800 mb-1">
              Rol
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full bg-white border border-green-200 text-slate-900 text-sm rounded-lg focus:ring-green-500 focus:border-green-500 block p-2.5 h-12"
            >
              {roles.map((r) => (
                <option key={r.name} value={r.name}>
                  {r.name}
                </option>
              ))}
            </select>
            
            {/* Show permissions for selected role */}
            {role && roles.length > 0 && (() => {
              const selectedRole = roles.find(r => r.name === role);
              if (!selectedRole) return null;
              
              return (
                <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield size={14} className="text-slate-600" />
                    <span className="text-xs font-semibold text-slate-700">
                      Permisos de este rol ({selectedRole.permissions.length})
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mb-2">{selectedRole.description}</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedRole.permissions.slice(0, 8).map((perm) => (
                      <span
                        key={perm}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-700"
                      >
                        {perm.split('.')[1]}
                      </span>
                    ))}
                    {selectedRole.permissions.length > 8 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-slate-200 text-slate-600">
                        +{selectedRole.permissions.length - 8} más
                      </span>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Botones */}
          <div className="flex items-center justify-between gap-4 pt-4 border-t border-green-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-green-300 text-green-600 font-bold rounded-lg hover:bg-green-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`
                flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white font-bold transition-all duration-300
                ${isLoading
                  ? 'bg-green-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 hover:shadow-lg hover:-translate-y-0.5'
                }
              `}
            >
              <Save className="h-5 w-5" />
              {isLoading ? 'Guardando...' : (userId ? 'Actualizar Usuario' : 'Crear Usuario')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
